import { useMemo } from 'react'
import { currentBooklet, usableBooklets, type Booklet } from '@shared/model'
import { useBooklets } from './useBooklets'
import { useMyCodes } from './useMyCodes'

/**
 * Det häfte användaren är "i" just nu: den pågående perioden hen äger ett
 * häfte i, annars den närmaste som ännu inte börjat. Används av sidorna som
 * inte har någon egen periodväljare.
 *
 * Utgångna perioder räknas inte – se `usableBooklets` i shared/model.ts.
 */
export function useCurrentBooklet(): {
  booklet: Booklet | null
  owned: number
  /**
   * Den senast utgångna perioden man ägt, när det inte finns någon aktuell.
   *
   * Finns för att vyerna ska kunna säga "perioden är slut" i stället för
   * "aktivera din kod först". Det senare är fel råd till någon vars kod är
   * förbrukad, och ett råd som inte går att följa är värre än inget.
   */
  lastExpired: Booklet | null
  loading: boolean
  error: Error | null
} {
  const { booklets, loading: bookletsLoading, error: bookletsError } = useBooklets()
  const { codesByBooklet, loading: codesLoading, error: codesError } = useMyCodes()

  const { booklet, lastExpired } = useMemo(() => {
    const owned = booklets.filter((item) => (codesByBooklet[item.id]?.length ?? 0) > 0)
    const usable = usableBooklets(owned)
    return {
      booklet: currentBooklet(owned),
      lastExpired: usable.length > 0 ? null : (owned[0] ?? null),
    }
  }, [booklets, codesByBooklet])

  return {
    booklet,
    owned: booklet ? (codesByBooklet[booklet.id]?.length ?? 0) : 0,
    lastExpired,
    loading: bookletsLoading || codesLoading,
    error: bookletsError ?? codesError,
  }
}
